import {
    Box,
    Button,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    HStack,
    Icon,
    Input,
    InputGroup,
    InputLeftElement,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    SimpleGrid,
    Switch,
    Text,
    Textarea,
    useColorModeValue,
    useToast,
} from "@chakra-ui/react";
import React, {useEffect, useMemo, useState} from "react";
import {FiCalendar, FiClock, FiDollarSign, FiPercent} from "react-icons/fi";
import {savePayment} from "../Services";

// Default VAT rate applied when the "VAT applicable" switch is on. Kept editable
// in the form so the admin can override per payment.
const DEFAULT_VAT_RATE = 13;

const todayYmd = () => new Date().toISOString().slice(0, 10);
const nowHm = () => new Date().toTimeString().slice(0, 5);

const buildInitialState = () => ({
    paymentDate: todayYmd(),
    paymentTime: nowHm(),
    amount: "",
    discount: "",
    vatApplicable: false,
    vatPercent: DEFAULT_VAT_RATE,
    numberOfYears: "1",
    remarks: "",
});

const toNumber = (value) => {
    const n = parseFloat(value);
    return isNaN(n) ? 0 : n;
};

function AddPaymentModal({isOpen, onClose, selectedSchool, onSaved}) {
    const toast = useToast();

    const modalBg = useColorModeValue("white", "gray.800");
    const sectionBg = useColorModeValue("gray.50", "whiteAlpha.100");
    const borderColor = useColorModeValue("gray.200", "whiteAlpha.200");
    const mutedText = useColorModeValue("gray.500", "gray.400");

    const [form, setForm] = useState(buildInitialState);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Reset the form each time the modal opens so a previous entry never lingers.
    useEffect(() => {
        if (isOpen) {
            setForm(buildInitialState());
            setErrors({});
        }
    }, [isOpen]);

    // Derived totals: subtotal after discount, VAT amount, grand total. Kept in a
    // memo so the summary updates live as the admin types.
    const totals = useMemo(() => {
        const amount = toNumber(form.amount);
        const discount = toNumber(form.discount);
        const taxable = Math.max(0, amount - discount);
        const vatAmount = form.vatApplicable
            ? (taxable * toNumber(form.vatPercent)) / 100
            : 0;
        return {
            amount,
            discount,
            taxable,
            vatAmount,
            total: taxable + vatAmount,
        };
    }, [form.amount, form.discount, form.vatApplicable, form.vatPercent]);

    const handleChange = (field) => (event) => {
        const value = event.target.value;
        setForm((current) => ({...current, [field]: value}));
        setErrors((current) => ({...current, [field]: undefined}));
    };

    const validate = () => {
        const next = {};
        if (!form.paymentDate) next.paymentDate = "Payment date is required.";
        if (!form.paymentTime) next.paymentTime = "Payment time is required.";
        if (form.amount === "" || toNumber(form.amount) <= 0) next.amount = "Enter a valid amount.";
        if (form.discount !== "" && toNumber(form.discount) < 0) next.discount = "Discount can't be negative.";
        if (toNumber(form.discount) > toNumber(form.amount)) next.discount = "Discount can't exceed the amount.";
        if (form.vatApplicable && (form.vatPercent === "" || toNumber(form.vatPercent) < 0)) {
            next.vatPercent = "Enter a valid VAT %.";
        }
        if (form.numberOfYears === "" || toNumber(form.numberOfYears) <= 0) {
            next.numberOfYears = "Enter number of years.";
        }
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        const request = {
            schoolId: selectedSchool,
            paymentDate: form.paymentDate,
            paymentTime: form.paymentTime,
            amount: totals.amount,
            discount: totals.discount,
            vatApplicable: form.vatApplicable,
            vatPercent: form.vatApplicable ? toNumber(form.vatPercent) : 0,
            vatAmount: totals.vatAmount,
            totalAmount: totals.total,
            numberOfYears: toNumber(form.numberOfYears),
            remarks: form.remarks?.trim() || "",
        };

        setIsSubmitting(true);
        try {
            const response = await savePayment(request);
            if (response.status === 200 || response.status === 201) {
                toast({
                    title: "Success!",
                    description: "Payment recorded successfully.",
                    status: "success",
                    duration: 5000,
                    isClosable: true,
                });
                onSaved && onSaved();
                onClose();
            }
        } catch (e) {
            console.error("Error while saving payment e:", e);
            toast({
                title: "Error!",
                description: e?.errorMessage || e?.message || "Failed to record payment. Please try again.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} isCentered size="xl" scrollBehavior="inside">
            <ModalOverlay/>
            <ModalContent bg={modalBg} borderRadius="16px">
                <ModalHeader>Add Payment</ModalHeader>
                <ModalCloseButton/>
                <ModalBody>
                    <SimpleGrid columns={{base: 1, md: 2}} spacing="16px">
                        <FormControl isRequired isInvalid={!!errors.paymentDate}>
                            <FormLabel fontSize="sm">Payment Date</FormLabel>
                            <InputGroup>
                                <InputLeftElement pointerEvents="none" children={<Icon as={FiCalendar} color="gray.400"/>}/>
                                <Input type="date" value={form.paymentDate} onChange={handleChange("paymentDate")}/>
                            </InputGroup>
                            <FormErrorMessage>{errors.paymentDate}</FormErrorMessage>
                        </FormControl>

                        <FormControl isRequired isInvalid={!!errors.paymentTime}>
                            <FormLabel fontSize="sm">Payment Time</FormLabel>
                            <InputGroup>
                                <InputLeftElement pointerEvents="none" children={<Icon as={FiClock} color="gray.400"/>}/>
                                <Input type="time" value={form.paymentTime} onChange={handleChange("paymentTime")}/>
                            </InputGroup>
                            <FormErrorMessage>{errors.paymentTime}</FormErrorMessage>
                        </FormControl>

                        <FormControl isRequired isInvalid={!!errors.amount}>
                            <FormLabel fontSize="sm">Amount</FormLabel>
                            <InputGroup>
                                <InputLeftElement pointerEvents="none" children={<Icon as={FiDollarSign} color="gray.400"/>}/>
                                <Input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    placeholder="0.00"
                                    value={form.amount}
                                    onChange={handleChange("amount")}
                                />
                            </InputGroup>
                            <FormErrorMessage>{errors.amount}</FormErrorMessage>
                        </FormControl>

                        <FormControl isInvalid={!!errors.discount}>
                            <FormLabel fontSize="sm">Discount</FormLabel>
                            <InputGroup>
                                <InputLeftElement pointerEvents="none" children={<Icon as={FiDollarSign} color="gray.400"/>}/>
                                <Input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    placeholder="0.00"
                                    value={form.discount}
                                    onChange={handleChange("discount")}
                                />
                            </InputGroup>
                            <FormErrorMessage>{errors.discount}</FormErrorMessage>
                        </FormControl>

                        <FormControl isRequired isInvalid={!!errors.numberOfYears}>
                            <FormLabel fontSize="sm">Number of Years</FormLabel>
                            <Input
                                type="number"
                                min="1"
                                step="1"
                                placeholder="1"
                                value={form.numberOfYears}
                                onChange={handleChange("numberOfYears")}
                            />
                            <FormErrorMessage>{errors.numberOfYears}</FormErrorMessage>
                        </FormControl>

                        <FormControl>
                            <FormLabel fontSize="sm" mb="10px">VAT applicable</FormLabel>
                            <HStack spacing="12px">
                                <Switch
                                    colorScheme="blue"
                                    isChecked={form.vatApplicable}
                                    onChange={(e) => setForm((c) => ({...c, vatApplicable: e.target.checked}))}
                                />
                                <InputGroup maxW="120px" opacity={form.vatApplicable ? 1 : 0.5}>
                                    <InputLeftElement pointerEvents="none" children={<Icon as={FiPercent} color="gray.400"/>}/>
                                    <Input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={form.vatPercent}
                                        onChange={handleChange("vatPercent")}
                                        isDisabled={!form.vatApplicable}
                                    />
                                </InputGroup>
                            </HStack>
                            <FormErrorMessage>{errors.vatPercent}</FormErrorMessage>
                        </FormControl>
                    </SimpleGrid>

                    <FormControl mt="16px">
                        <FormLabel fontSize="sm">Remarks</FormLabel>
                        <Textarea
                            placeholder="Optional notes about this payment"
                            value={form.remarks}
                            onChange={handleChange("remarks")}
                            rows={3}
                        />
                    </FormControl>

                    {/* Live total summary. */}
                    <Box mt="20px" bg={sectionBg} border="1px solid" borderColor={borderColor} borderRadius="12px" p="14px">
                        <Flex justify="space-between" mb="6px">
                            <Text fontSize="sm" color={mutedText}>Subtotal (after discount)</Text>
                            <Text fontSize="sm" fontWeight="medium">{totals.taxable.toFixed(2)}</Text>
                        </Flex>
                        <Flex justify="space-between" mb="6px">
                            <Text fontSize="sm" color={mutedText}>
                                VAT {form.vatApplicable ? `(${toNumber(form.vatPercent)}%)` : "(N/A)"}
                            </Text>
                            <Text fontSize="sm" fontWeight="medium">{totals.vatAmount.toFixed(2)}</Text>
                        </Flex>
                        <Flex justify="space-between" pt="8px" borderTop="1px solid" borderColor={borderColor}>
                            <Text fontSize="md" fontWeight="bold">Total</Text>
                            <Text fontSize="md" fontWeight="bold" color="blue.400">{totals.total.toFixed(2)}</Text>
                        </Flex>
                    </Box>
                </ModalBody>
                <ModalFooter>
                    <Button variant="ghost" mr="12px" onClick={onClose} isDisabled={isSubmitting}>
                        Cancel
                    </Button>
                    <Button colorScheme="blue" onClick={handleSubmit} isLoading={isSubmitting} loadingText="Saving">
                        Save Payment
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default AddPaymentModal;
